import { useEffect, useState } from "react";

/**
 * Hook for managing table sorting with localStorage persistence.
 * We should consider moving the localstorage piece to the backend.
 *
 * @template SortField - The type of field names that can be sorted
 * @param storageKey - The key to use for storing the sort state in localStorage
 * @param initialSort - The initial sort state
 * @returns An object containing the sort state, a function to update the sort state, and a function to get the order field
 */
export function useTableSort<SortField extends string>(
  storageKey: string,
  initialSort?: { field?: SortField; asc?: boolean },
): {
  currentSort: { field?: SortField; asc?: boolean };
  updateSortState: (field: SortField) => void;
  getOrderField: () => string | undefined;
} {
  // Load initial sort from localStorage or use default
  const [currentSort, setCurrentSort] = useState<{
    field?: SortField;
    asc?: boolean;
  }>(() => {
    const initial = initialSort || { field: undefined, asc: undefined };
    if (typeof window === "undefined") return initial;

    try {
      const stored = localStorage.getItem(storageKey);
      return stored ? JSON.parse(stored) : initial;
    } catch {
      return initial;
    }
  });

  // Save sort to localStorage when it changes
  useEffect(() => {
    if (typeof window === "undefined") return;

    try {
      localStorage.setItem(storageKey, JSON.stringify(currentSort));
    } catch (error) {
      console.error("Failed to save sort state:", error);
    }
  }, [currentSort, storageKey]);

  // Update the sort state
  function updateSortState(field: SortField) {
    setCurrentSort((prevSort) => {
      // Different field - use descending
      if (field !== prevSort.field) return { field, asc: false };

      // Same field, toggle direction or clear
      if (!prevSort.asc) return { field, asc: true };

      return { field: undefined, asc: undefined };
    });
  }

  // Utility function to get the order field for the backend
  const getOrderField = () => {
    const { field, asc } = currentSort;
    if (!field) return undefined;
    return asc ? field : `-${field}`;
  };

  return { currentSort, updateSortState, getOrderField };
}
